// --------------------------------------------------------
// TRIAGEM FIELD - TriagemField
// --------------------------------------------------------

import React, { useEffect, useState, useCallback } from 'react'
import { H3, Text, Table } from '../../../../../../../components/UI/generalLayout/Layout.styled';
import { MediumInput, AddButton } from '../DetailsAttendanceRecord.styled'
import { TextField, Select, MenuItem, FormControl, InputLabel, IconButton, CircularProgress, Box } from '@mui/material';
import { VitalSignService } from '../../../../../../../core/http/service/VitalSignService';
import VitalSign from '../../../../../../../models/core/careLine/VitalSign';
import AtendimentoSinalVital from '../../../../../../../models/core/appointment/AtendimentoSinalVital';
import { BiTrash } from 'react-icons/bi';
import moment from 'moment';
import InputMask from 'react-input-mask';
import Person from '../../../../../../../models/core/userRole/Person';
import Colors from '../../../../../../../constants/Colors';
import { GenericSolidButton } from '../../../../../../../components/UI/buttons/Button.styled';

interface ITriagemField {
    triagem: {
        sinaisVitais: string;
        sinaisVitaisList: any[];
    };
    isViewState: boolean;
    setTriagemObject: (value: any) => void;
    pessoa?: Person;
    onSalvar?: () => void;
    acessoProntuario?: boolean;
    isLoading?: boolean;
}

const TriagemField = ({ triagem, setTriagemObject, isViewState, pessoa, onSalvar, acessoProntuario, isLoading }: ITriagemField) => {
    const [allVitalSigns, setAllVitalSigns] = useState<VitalSign[]>([]);
    const [loading, setLoading] = useState(false);

    const vitalSignService = new VitalSignService();

    const fetchVitalSigns = useCallback(async () => {
        setLoading(true);
        try {
            const resp: any = await vitalSignService.getAll();
            if (resp.data && resp.data.data) {
                setAllVitalSigns(resp.data.data);
                
                // If it's not view state and we don't have signs in the list, load defaults
                if (!isViewState && triagem.sinaisVitaisList.length === 0) {
                    const defaults = resp.data.data.filter((vs: any) => vs.padrao && !vs.inativo);
                    const initialList = defaults.map((vs: any) => ({
                        sinalVitalId: vs.id,
                        sinalVitalNome: vs.nome,
                        valor: "",
                        valorSecundario: "",
                        unidade: vs.unidadePadrao || "",
                        unidadeTouched: false,
                        local: "",
                        instrumento: vs.instrumentos?.find((i: any) => i.padrao)?.nome || "",
                        tipoCampo: vs.tipoCampo,
                        formula: vs.formula,
                        unidades: vs.unidades,
                        instrumentos: vs.instrumentos,
                        locais: vs.locais
                    }));
                    setTriagemObject({ ...triagem, sinaisVitaisList: initialList });
                }
            }
        } catch (error) {
            console.error("Error fetching vital signs:", error);
        } finally {
            setLoading(false);
        }
    }, [isViewState, triagem.sinaisVitaisList.length]);

    useEffect(() => {
        fetchVitalSigns();
    }, []);

    const normalizeValue = (valor: any, unidade: string) => {
        if (valor === undefined || valor === null || valor === "") return null;
        
        let valStr = valor.toString().replace(/_/g, '').trim();
        
        // Handle decimal separators (convert comma to dot)
        if (valStr.includes(',') && !valStr.includes('.')) {
            valStr = valStr.replace(',', '.');
        }

        // Keep only digits and the first dot found
        let cleanValue = "";
        let foundDot = false;
        for (let i = 0; i < valStr.length; i++) {
            const char = valStr[i];
            if (/[0-9]/.test(char)) {
                cleanValue += char;
            } else if ((char === '.' || char === ',') && !foundDot) {
                cleanValue += '.';
                foundDot = true;
            }
        }

        let numValue = parseFloat(cleanValue);
        if (isNaN(numValue)) return null;

        const unit = (unidade || "").toLowerCase().trim();

        // Height Normalization (Base: Meters)
        if (unit.includes('cm') || unit.includes('cent')) {
            return numValue / 100;
        }
        
        if (unit === 'm' || unit.startsWith('metr')) {
            // Safety: if user typed "171" but unit is "m", treat as cm
            if (numValue > 10) return numValue / 100;
            return numValue;
        }

        // Weight Normalization (Base: Kilograms)
        if (unit === 'g' || unit === 'grama' || unit === 'gramas') return numValue / 1000;
        if (unit === 'mg' || unit.includes('mili')) return numValue / 1000000;

        return numValue;
    };

    const evaluateFormula = (formula: string, currentList: any[]) => {
        if (!formula) return "";

        let evaluated = formula;
        const matches = formula.match(/\[(.*?)\]/g);

        if (matches) {
            for (const match of matches) {
                const varName = match.replace('[', '').replace(']', '').toUpperCase();
                let value: any = "";

                if (varName === 'IDADE') {
                    if (pessoa?.dataNascimento) {
                        value = moment().diff(pessoa.dataNascimento, 'years');
                    }
                } else if (varName === 'SEXO') {
                    value = pessoa?.Genero === 'M' ? 1 : 0;
                } else {
                    // Search in vital signs list
                    const sv = currentList
                        .filter(s => s.sinalVitalNome.toUpperCase().includes(varName) && (s.valor !== undefined && s.valor !== null && s.valor !== ""))
                        .sort((a, b) => {
                            const nameA = a.sinalVitalNome.toUpperCase();
                            const nameB = b.sinalVitalNome.toUpperCase();
                            
                            // 1. Priority: Exact name match
                            const aExact = nameA === varName;
                            const bExact = nameB === varName;
                            if (aExact && !bExact) return -1;
                            if (!aExact && bExact) return 1;

                            // 2. Priority: New (unsaved) values
                            const aIsNew = !a.dataInclusao || a.id === "0" || a.id === "";
                            const bIsNew = !b.dataInclusao || b.id === "0" || b.id === "";
                            if (aIsNew && !bIsNew) return -1;
                            if (!aIsNew && bIsNew) return 1;
                            
                            // 3. Priority: Newest date
                            if (a.dataInclusao && b.dataInclusao) {
                                return new Date(b.dataInclusao).getTime() - new Date(a.dataInclusao).getTime();
                            }
                            return 0;
                        })[0];
                    
                    if (sv) {
                        const normalized = normalizeValue(sv.valor, sv.unidade || "");
                        if (normalized !== null) {
                            value = normalized;
                        }
                    }
                }

                if (value === "" || value === undefined || value === null) return ""; 
                evaluated = evaluated.replace(match, value);
            }
        }

        try {
            // eslint-disable-next-line no-eval
            const result = eval(evaluated);
            if (isNaN(result) || !isFinite(result)) return "";
            
            if (result > 0 && result < 0.01) return result.toString().replace('.', ',');
            return result.toFixed(2).replace('.', ',');
        } catch (e) {
            console.error("Error evaluating formula:", formula, e);
            return "";
        }
    };

    const recalculateAll = (list: any[]) => {
        list.forEach(item => {
            if (item.tipoCampo === 'calculado') {
                if (!item.instrumento) {
                    item.instrumento = "Cálculo Automático";
                }

                if (item.formula) {
                    item.valor = evaluateFormula(item.formula, list);
                } else if (item.sinalVitalNome.toUpperCase().includes('IMC')) {
                    const findSign = (name: string) => list
                        .filter(s => s.sinalVitalNome.toUpperCase().includes(name) && (s.valor !== undefined && s.valor !== null && s.valor !== ""))
                        .sort((a, b) => {
                            const nameA = a.sinalVitalNome.toUpperCase();
                            const nameB = b.sinalVitalNome.toUpperCase();
                            
                            const aExact = nameA === name;
                            const bExact = nameB === name;
                            if (aExact && !bExact) return -1;
                            if (!aExact && bExact) return 1;

                            const aIsNew = !a.dataInclusao || a.id === "0" || a.id === "";
                            const bIsNew = !b.dataInclusao || b.id === "0" || b.id === "";
                            if (aIsNew && !bIsNew) return -1;
                            if (!aIsNew && bIsNew) return 1;

                            if (a.dataInclusao && b.dataInclusao) {
                                return new Date(b.dataInclusao).getTime() - new Date(a.dataInclusao).getTime();
                            }
                            return 0;
                        })[0];

                    const pesoObj = findSign('PESO');
                    const alturaObj = findSign('ALTURA');

                    if (pesoObj && alturaObj) {
                        const p = normalizeValue(pesoObj.valor, pesoObj.unidade || "");
                        const a = normalizeValue(alturaObj.valor, alturaObj.unidade || "");
                        
                        if (p !== null && a !== null && p > 0 && a > 0) {
                            const imcValue = p / (a * a);
                            if (imcValue > 0 && imcValue < 0.01) {
                                item.valor = imcValue.toString().replace('.', ',');
                            } else {
                                item.valor = imcValue.toFixed(2).replace('.', ',');
                            }
                        } else {
                            item.valor = "";
                        }
                    } else {
                        item.valor = "";
                    }
                }
            }
        });
        return list;
    };

    const handleValueChange = (index: number, fieldOrObject: string | object, value?: any) => {
        const newList = [...triagem.sinaisVitaisList];
        
        if (typeof fieldOrObject === 'object') {
            newList[index] = { ...newList[index], ...fieldOrObject };
        } else {
            newList[index][fieldOrObject as string] = value;
        }

        const updatedList = recalculateAll(newList);
        setTriagemObject({ ...triagem, sinaisVitaisList: updatedList });
    };

    const addNewVitalSign = () => {
        setTriagemObject({
            ...triagem,
            sinaisVitaisList: [
                ...triagem.sinaisVitaisList,
                {
                    sinalVitalId: "",
                    sinalVitalNome: "",
                    valor: "",
                    valorSecundario: "",
                    unidade: "",
                    unidadeTouched: false,
                    local: "",
                    instrumento: "",
                    tipoCampo: "numerico"
                }
            ]
        });
    };

    const removeVitalSign = (index: number) => {
        const newList = [...triagem.sinaisVitaisList];
        newList.splice(index, 1);
        setTriagemObject({ ...triagem, sinaisVitaisList: newList });
    };

    const renderSavedGroups = () => {
        const savedSigns = triagem.sinaisVitaisList.filter(sv => !!sv.id && sv.id !== "0");
        
        const grouped = savedSigns.reduce((acc: any, sv: any) => {
            const dateKey = moment(sv.dataInclusao).format('YYYY-MM-DD HH:mm');
            const key = `${dateKey}_${sv.nomeProfissional}`;
            if (!acc[key]) acc[key] = [];
            acc[key].push(sv);
            return acc;
        }, {});

        const sortedKeys = Object.keys(grouped).sort((a, b) => b.localeCompare(a));

        return sortedKeys.map(key => {
            const group = grouped[key];
            const first = group[0];
            return (
                <div key={key} style={{ marginBottom: 25 }}>
                    <div style={{ 
                        backgroundColor: '#EEF4FF', 
                        padding: '8px 16px', 
                        borderRadius: '8px 8px 0 0', 
                        border: '1px solid #D1E9FF',
                        borderBottom: 'none',
                        display: 'flex', 
                        flexWrap: 'wrap',
                        gap: '10px',
                        alignItems: 'center' 
                    }}>
                        <Text bold fontSize="14px" style={{ margin: 0, color: '#175CD3' }}>
                            Horário - {moment(first.dataInclusao).format('HH:mm')} - {first.nomeProfissional} - {first.conselhoProfissional}{first.conselhoProfissional && first.registroConselhoProfissional ? ': ' : ''}{first.registroConselhoProfissional} - {first.cargoProfissional}
                        </Text>
                    </div>
                    <div style={{ border: '1px solid #EAECF0', borderRadius: '0 0 8px 8px', overflow: 'hidden' }}>
                        <Table style={{ borderCollapse: 'collapse' }}>
                            <thead>
                                <tr style={{ backgroundColor: '#FCFCFD', borderBottom: '1px solid #EAECF0' }}>
                                    <th style={{ padding: '12px 16px', textAlign: 'left' }}><Text bold fontSize="12px" color="#667085">Sinal Vital</Text></th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left' }}><Text bold fontSize="12px" color="#667085">Valor</Text></th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left' }}><Text bold fontSize="12px" color="#667085">Unidade</Text></th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left' }}><Text bold fontSize="12px" color="#667085">Local</Text></th>
                                    <th style={{ padding: '12px 16px', textAlign: 'left' }}><Text bold fontSize="12px" color="#667085">Instrumento</Text></th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...group]
                                    .sort((a: any, b: any) => {
                                        const configA = allVitalSigns.find(vs => vs.id?.toLowerCase() === a.sinalVitalId?.toLowerCase());
                                        const configB = allVitalSigns.find(vs => vs.id?.toLowerCase() === b.sinalVitalId?.toLowerCase());
                                        const ordemA = configA && configA.ordem !== null ? configA.ordem : 999;
                                        const ordemB = configB && configB.ordem !== null ? configB.ordem : 999;
                                        return ordemA - ordemB;
                                    })
                                    .map((sv: any, idx: number) => (
                                    <tr key={idx} style={{ borderBottom: idx === group.length - 1 ? 'none' : '1px solid #EAECF0' }}>
                                        <td style={{ padding: '12px 16px' }}><Text fontSize="14px">{sv.sinalVitalNome}</Text></td>
                                        <td style={{ padding: '12px 16px' }}>
                                            <Text fontSize="14px" bold>
                                                {sv.tipoCampo === 'pressaoarterial' 
                                                    ? `${sv.valor} mmHg (Máx) / ${sv.valorSecundario} mmHg (Mín)` 
                                                    : `${sv.valor}${sv.valorSecundario ? ` / ${sv.valorSecundario}` : ""}`}
                                            </Text>
                                        </td>
                                        <td style={{ padding: '12px 16px' }}><Text fontSize="14px">{sv.unidade}</Text></td>
                                        <td style={{ padding: '12px 16px' }}><Text fontSize="14px">{sv.local || "-"}</Text></td>
                                        <td style={{ padding: '12px 16px' }}><Text fontSize="14px">{sv.instrumento}</Text></td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                </div>
            );
        });
    };

    const getMaskForUnit = (mascara: string, unit: string) => {
        if (!mascara) return "";
        if (!mascara.includes(':')) return mascara; // Static mask

        const parts = mascara.split(';');
        const unitPart = parts.find(p => p.trim().toLowerCase().startsWith(unit.toLowerCase() + ':'));
        
        if (unitPart) {
            return unitPart.split(':')[1].trim();
        }

        return parts[0].includes(':') ? "" : parts[0]; // Fallback to first if no specific match
    };

    const renderVitalSignInput = (sv: any, index: number) => {
        const vsConfig = allVitalSigns.find(v => v.id?.toLowerCase() === sv.sinalVitalId?.toLowerCase());
        const currentMask = getMaskForUnit(sv.mascara || "", sv.unidade || "");
        const InputMaskAny: any = InputMask;

        return (
            <div key={index} style={{ display: 'flex', flexWrap: 'nowrap', gap: 10, marginBottom: 20, alignItems: 'center', backgroundColor: '#fff', padding: '15px', borderRadius: '8px', border: '1px solid #EAECF0', overflowX: 'auto' }}>
                <FormControl style={{ width: 250, flexShrink: 0 }}>
                    <InputLabel>Sinal Vital</InputLabel>
                    <Select
                        value={sv.sinalVitalId || ""}
                        label="Sinal Vital"
                        onChange={(e) => {
                            const selected = allVitalSigns.find(v => v.id?.toLowerCase() === e.target.value?.toLowerCase());
                            if (selected) {
                                handleValueChange(index, {
                                    sinalVitalId: selected.id,
                                    sinalVitalNome: selected.nome,
                                    unidade: selected.unidadePadrao || "",
                                    unidadeTouched: false,
                                    instrumento: selected.instrumentos?.find(i => i.padrao)?.nome || "",
                                    tipoCampo: selected.tipoCampo,
                                    mascara: selected.mascara,
                                    formula: selected.formula,
                                    unidades: selected.unidades,
                                    instrumentos: selected.instrumentos,
                                    locais: selected.locais
                                });
                            }
                        }}
                    >
                        {allVitalSigns.filter(vs => !vs.inativo).map(vs => (
                            <MenuItem key={vs.id!} value={vs.id!}>{vs.nome}</MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <div style={{ width: 220, display: 'flex', justifyContent: 'center' }}>
                    {sv.tipoCampo === 'pressaoarterial' ? (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                            <TextField
                                label="Sist."
                                style={{ width: 85 }}
                                value={sv.valor || ""}
                                onChange={(e) => handleValueChange(index, 'valor', e.target.value)}
                            />
                            <span style={{ fontSize: 20, color: '#667085' }}>/</span>
                            <TextField
                                label="Diast."
                                style={{ width: 85 }}
                                value={sv.valorSecundario || ""}
                                onChange={(e) => handleValueChange(index, 'valorSecundario', e.target.value)}
                            />
                        </div>
                    ) : (
                        currentMask ? (
                            <InputMaskAny
                                mask={currentMask}
                                value={sv.valor || ""}
                                disabled={sv.tipoCampo === 'calculado'}
                                onChange={(e: any) => handleValueChange(index, 'valor', e.target.value)}
                            >
                                <TextField
                                    label="Valor"
                                    fullWidth
                                />
                            </InputMaskAny>
                        ) : (
                            <TextField
                                label="Valor"
                                fullWidth
                                value={sv.valor || ""}
                                onChange={(e) => handleValueChange(index, 'valor', e.target.value)}
                                disabled={sv.tipoCampo === 'calculado'}
                            />
                        )
                    )}
                </div>

                <FormControl style={{ width: 150 }} error={sv.tipoCampo !== 'calculado' && sv.unidadeTouched && !sv.unidade}>
                    <InputLabel>Unidade</InputLabel>
                    <Select
                        value={sv.unidade || ""}
                        label="Unidade"
                        onOpen={() => handleValueChange(index, 'unidadeTouched', true)}
                        onChange={(e) => handleValueChange(index, 'unidade', e.target.value)}
                    >
                        <MenuItem value="">Nenhum</MenuItem>
                        {(vsConfig?.unidades || sv.unidades || sv.unidade) && 
                            (vsConfig?.unidades || sv.unidades || sv.unidade).split(',').map((u: string) => (
                                <MenuItem key={u.trim()} value={u.trim()}>{u.trim()}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>

                <FormControl style={{ width: 180 }}>
                    <InputLabel>Local</InputLabel>
                    <Select
                        value={sv.local || ""}
                        label="Local"
                        onChange={(e) => handleValueChange(index, 'local', e.target.value)}
                    >
                        <MenuItem value="">Nenhum</MenuItem>
                        {(vsConfig?.locais || sv.locais || sv.local) && 
                            (vsConfig?.locais || sv.locais || sv.local).split(',').map((l: string) => (
                                <MenuItem key={l.trim()} value={l.trim()}>{l.trim()}</MenuItem>
                            ))
                        }
                    </Select>
                </FormControl>

                <FormControl style={{ width: 250 }} required>
                    <InputLabel>Instrumento</InputLabel>
                    <Select
                        value={sv.instrumento || ""}
                        label="Instrumento"
                        onChange={async (e) => {
                            const newInstrument = e.target.value;
                            let unidadesList = vsConfig?.unidades || sv.unidades || "";
                            let locaisList = vsConfig?.locais || sv.locais || "";

                            // The list endpoint (getAll) does not return the units/places config,
                            // so we fetch the full vital sign config to populate the options.
                            if (sv.sinalVitalId) {
                                try {
                                    const resp: any = await vitalSignService.getById(sv.sinalVitalId);
                                    if (resp && resp.data && resp.data.data) {
                                        const fetchedVs = new VitalSign().fromJSON(resp.data.data);
                                        unidadesList = fetchedVs.unidades || "";
                                        locaisList = fetchedVs.locais || "";
                                        // Keep the cached config in sync with the fresh data
                                        setAllVitalSigns(prev => prev.map(item => item.id?.toLowerCase() === fetchedVs.id?.toLowerCase() ? fetchedVs : item));
                                    }
                                } catch (error) {
                                    console.error("Error querying vital sign by ID:", error);
                                }
                            }

                            handleValueChange(index, {
                                instrumento: newInstrument,
                                unidade: "", // Reset unit so the user must re-select the proper one for this instrument
                                unidadeTouched: true, // Force error display until a unit is chosen again
                                unidades: unidadesList,
                                locais: locaisList
                            });
                        }}
                    >
                        {vsConfig && Array.isArray(vsConfig.instrumentos) && vsConfig.instrumentos.length > 0 ? (
                            vsConfig.instrumentos.map((i: any) => (
                                <MenuItem key={i.id || i.nome} value={i.nome}>{i.nome}</MenuItem>
                            ))
                        ) : (
                            sv.instrumento ? <MenuItem value={sv.instrumento}>{sv.instrumento}</MenuItem> : <MenuItem value="">Nenhum</MenuItem>
                        )}
                    </Select>
                </FormControl>

                <IconButton onClick={() => removeVitalSign(index)} color="error" title="Remover">
                    <BiTrash />
                </IconButton>
            </div>
        );
    };

    const hasUnsavedVitalSigns = triagem.sinaisVitaisList.some(
        (sv: any) =>
            (!sv.id || sv.id === "0") &&
            (Boolean(sv.valor?.toString().trim()) || Boolean(sv.valorSecundario?.toString().trim()))
    );

    return (
        <>
            <div style={{ marginBottom: 30 }}>
                <MediumInput
                    disabled={isViewState}
                    value={triagem.sinaisVitais}
                    onChange={(event: any) => { setTriagemObject({ ...triagem, sinaisVitais: event.target.value }) }}
                    placeholder="Anotações de enfermagem"
                />

                <div style={{ marginTop: 10 }}>
                    {renderSavedGroups()}
                </div>
                
                {!isViewState && (
                    <>
                        <H3 style={{ marginBottom: 15, color: '#475467', marginTop: 20 }}>Sinais vitais</H3>
                        {triagem.sinaisVitaisList
                            .filter(sv => !sv.id || sv.id === "0")
                            .map((sv, idx) => {
                                const originalIndex = triagem.sinaisVitaisList.indexOf(sv);
                                return renderVitalSignInput(sv, originalIndex);
                            })
                        }
                        
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, marginTop: 15, marginBottom: 20 }}>
                            <AddButton onClick={addNewVitalSign} style={{ backgroundColor: '#fff', color: '#344054', border: '1px solid #D0D5DD', margin: 0 }}>
                                + Adicionar outro sinal vital
                            </AddButton>

                            {onSalvar && !acessoProntuario && hasUnsavedVitalSigns && (
                                <GenericSolidButton
                                    color={Colors.SPM600}
                                    width={25}
                                    disabled={isLoading}
                                    onClick={onSalvar}
                                    style={{ minWidth: 140 }}
                                >
                                    {isLoading ? (
                                        <div style={{ display: "flex", alignItems: "center", gap: 8, justifyContent: "center" }}>
                                            <CircularProgress size={16} sx={{ color: "white" }} />
                                            <span>Salvando...</span>
                                        </div>
                                    ) : (
                                        "Salvar"
                                    )}
                                </GenericSolidButton>
                            )}
                        </div>
                    </>
                )}

            </div>
        </>
    )

}

export default TriagemField
